import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FileWidget, Tabs} from '@core/components';
import {Attachment, SupplierDetail, SupplierNote, SupplierRow} from '@core/types';
import {AttachmentService, SupplierService} from '@core/services';

@Component({
  selector: 'core-modal-supplier-file',
  imports: [
    FileWidget,
    Tabs,
    FormsModule
  ],
  templateUrl: './supplier-file.html',
  styleUrl: './supplier-file.scss',
  standalone: true,
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;padding: 0 1.5rem 1.5rem'
  }
})
export class SupplierFile implements OnInit, OnDestroy {
  @Output()
  edit = new EventEmitter();
  @Input() supplier: SupplierRow | null = null;

  detail: SupplierDetail | null = null;
  notes: SupplierNote[] = [];
  attachments: Attachment[] = [];
  isLoadingDetail = false;
  isLoadingLogo = false;
  detailError = '';
  isLoadingNotes = false;
  notesError = '';
  isLoadingAttachments = false;
  isSubmittingNote = false;
  deletingNoteIds: Array<string | number> = [];
  isRefreshingNotes = false;
  attachmentsError = '';
  placeholderImage = '/assets/images/placeholders/image-placeholder.png';
  logoBlobUrl = '';
  noteDraft = '';
  private supplierService = inject(SupplierService);
  private attachmentService = inject(AttachmentService);

  ngOnInit() {
    if (!this.supplier?.id) return;
    this.loadDetail(this.supplier.id);
    this.loadNotes(this.supplier.id);
    this.loadAttachments(this.supplier.id);
  }

  ngOnDestroy() {
    this.revokeLogoBlobUrl();
  }

  readonly tabSkeletonItems = [1, 2, 3];

  formatFileSize(size?: number): string {
    if (!size) return '0 KB';
    if (size < 1024) return `${size} B`;
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  }

  formatDate(value?: string) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('es-ES');
  }

  private async loadDetail(supplierId: string | number) {
    this.isLoadingDetail = true;
    this.detailError = '';
    const response = await this.supplierService.getSupplierDetail(supplierId, {skipLoader: true});
    this.isLoadingDetail = false;

    if (!response.success || !response.result) {
      this.detailError = response.message || 'No se pudo cargar la ficha del proveedor.';
      return;
    }

    this.detail = response.result;
  }

  private async loadNotes(supplierId: string | number) {
    this.isLoadingNotes = true;
    this.notesError = '';
    const response = await this.supplierService.getSupplierNotes(supplierId, {skipLoader: true});
    this.isLoadingNotes = false;

    if (!response.success || !response.result) {
      this.notesError = response.message || 'No se pudieron cargar las notas.';
      return;
    }

    this.notes = response.result;
  }

  private async loadAttachments(supplierId: string | number) {
    this.isLoadingAttachments = true;
    this.attachmentsError = '';
    const response = await this.supplierService.getSupplierAttachments(supplierId, {skipLoader: true});
    this.isLoadingAttachments = false;

    if (!response.success || !response.result) {
      this.attachmentsError = response.message || 'No se pudieron cargar los adjuntos.';
      return;
    }

    this.attachments = response.result;
    await this.resolveLogo();
  }

  get documentationAttachments() {
    const logoFileName = this.logoFileName();
    if (!logoFileName) {
      return this.attachments.filter((attachment, index) => !this.isFallbackLogoAttachment(attachment, index));
    }

    return this.attachments.filter(attachment => attachment.fileName !== logoFileName);
  }

  isDeletingNote(noteId: string | number) {
    return this.deletingNoteIds.some(id => String(id) === String(noteId));
  }

  authorName(note: SupplierNote) {
    return note.author?.name || 'Sin autor';
  }

  async downloadAttachment(attachment: Attachment) {
    if (!attachment?.id) return;

    const response = await this.attachmentService.downloadAttachment(attachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.attachmentsError = response.message || 'No se pudo descargar el adjunto.';
      return;
    }

    this.attachmentsError = '';
    const url = URL.createObjectURL(response.result);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName || 'adjunto';
    link.click();
    URL.revokeObjectURL(url);
  }

  async submitNote() {
    if (!this.supplier?.id || this.isSubmittingNote) return;
    const content = this.noteDraft.trim();
    if (!content) {
      this.notesError = 'Escribe una nota antes de guardar.';
      return;
    }

    this.isSubmittingNote = true;
    this.notesError = '';
    this.isRefreshingNotes = true;
    const response = await this.supplierService.addSupplierNote(this.supplier.id, content, {skipLoader: true});

    if (!response.success || !response.result) {
      this.isSubmittingNote = false;
      this.isRefreshingNotes = false;
      this.notesError = response.message || 'No se pudo agregar la nota.';
      return;
    }

    this.noteDraft = '';
    await this.loadNotes(this.supplier.id);
    this.isSubmittingNote = false;
    this.isRefreshingNotes = false;
  }

  async deleteNote(noteId: string | number) {
    if (this.isDeletingNote(noteId)) return;

    this.deletingNoteIds = [...this.deletingNoteIds, noteId];
    const response = await this.supplierService.deleteSupplierNote(noteId, {skipLoader: true});
    this.deletingNoteIds = this.deletingNoteIds.filter(id => String(id) !== String(noteId));

    if (!response.success) {
      this.notesError = response.message || 'No se pudo eliminar la nota.';
      return;
    }

    this.notes = this.notes.filter(note => String(note.id) !== String(noteId));
  }

  logoSrc() {
    return this.logoBlobUrl || this.placeholderImage;
  }

  handleLogoError(event: Event) {
    const target = event.target as HTMLImageElement | null;
    if (target) {
      target.src = this.placeholderImage;
    }
  }

  private async resolveLogo() {
    this.revokeLogoBlobUrl();
    this.isLoadingLogo = true;
    const logoAttachment = this.getLogoAttachment();
    if (!logoAttachment?.id) {
      this.isLoadingLogo = false;
      return;
    }

    const response = await this.attachmentService.downloadAttachment(logoAttachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.isLoadingLogo = false;
      return;
    }

    this.logoBlobUrl = URL.createObjectURL(response.result);
    this.isLoadingLogo = false;
  }

  private isImageAttachment(attachment: Attachment) {
    return (attachment.mimeType || '').startsWith('image/');
  }

  private logoFileName() {
    return this.detail?.metadata?.['logoFileName'] || '';
  }

  private getLogoAttachment() {
    const logoFileName = this.logoFileName();
    if (logoFileName) {
      return this.attachments.find(attachment => attachment.fileName === logoFileName) || null;
    }

    return this.attachments.find(attachment => this.isImageAttachment(attachment)) || null;
  }

  private isFallbackLogoAttachment(attachment: Attachment, index: number) {
    if (!this.isImageAttachment(attachment)) {
      return false;
    }

    const firstImage = this.attachments.findIndex(item => this.isImageAttachment(item));
    return firstImage === index;
  }

  private revokeLogoBlobUrl() {
    if (!this.logoBlobUrl) return;
    URL.revokeObjectURL(this.logoBlobUrl);
    this.logoBlobUrl = '';
  }
}
